/*
 * Beyond the Fridge — Keep Calm and Carry On generator (screen 14).
 *
 * Ported from the design's Component logic. The board face is the supplied master artwork,
 * Keep_calm_red_aw_lg.svg: its 2154.3 × 2863 artboard is the 75 × 100 cm portrait board. The
 * crown and the four fixed words are the master's own outlined paths, stored as six separable
 * groups — crown, KEEP, CALM, AND, CARRY, ON — each with the exact bounding box getBBox()
 * reports, so the rows can be re-stacked without a single path coordinate changing. Every group
 * is only ever translated and uniformly scaled; nothing is stretched or re-spaced.
 *
 * Unpersonalised portrait is the master at its own coordinates. Personalising joins CARRY and
 * ON onto one line, which frees the row the added word needs. Landscape re-stacks into three
 * lines — KEEP CALM / AND / CARRY ON — so the lockup fills a wide board. The word space between
 * joined groups is derived from the cap height. Only the added word is live type, in Gill Sans
 * to match (named first, falling back).
 *
 * The board is shown on the Create Your Own three-layer render on the same plates as the town
 * board. Elements: <btf-keepcalm-board> (one board) and <btf-keepcalm-preview> (the page
 * controller) — see btf-town.js for the pattern.
 */

// Board sizes in cm, [width, height]
const CM = {
  Landscape: { Small: [55, 40], Medium: [75, 55], Large: [100, 75] },
  Portrait: { Small: [40, 55], Medium: [55, 75], Large: [75, 100] },
};

// The board rectangle inside each production plate, [left%, top%, width%, height%], from Create Your Own
const PROD = {
  Landscape: { ar: '1000 / 730', arNum: 1000 / 730, Large: [4.3, 2.9, 90.7, 93.2], Medium: [15.8, 15.6, 68.3, 68.6], Small: [25.0, 25.1, 49.9, 49.6] },
  Portrait: { ar: '1 / 1', arNum: 1, Large: [16.0, 4.3, 68.0, 90.6], Medium: [25.0, 15.8, 49.7, 68.3], Small: [31.6, 21.0, 36.2, 49.9] },
};

// The same for the room composites (all square)
const ROOM = {
  Landscape: { Large: [27.0, 9.6, 47.5, 35.6], Medium: [32.5, 18.9, 35.3, 25.7], Small: [36.5, 20.2, 31.2, 22.6] },
  Portrait: { Large: [33.4, 4.7, 35.2, 47.0], Medium: [36.8, 14.3, 25.7, 35.3], Small: [40.5, 16.0, 22.6, 31.2] },
};

const PLATE = { Small: 'sm', Medium: 'md', Large: 'lg' };

// Gill Sans is the product's face. It is not licensed into this project, so the stack names it
// first and falls back — correct on a machine that has it, an approximation elsewhere.
export const FACE = '"Gill Sans", "Gill Sans MT", "Gill Sans Nova", Calibri, "Trebuchet MS", sans-serif';

// The lettering and crest ink: the pale cream of the original print rather than a flat white.
// Pale grounds take the dark ink instead, where this would vanish.
const INK = '#fff6d2';
const DARK_INK = '#3a332a';

// Colourways. Brown, green, blue, grey, black, red, cream and teal are the design's; pink is
// sampled from the live product photograph and white is the plain white board. A product's
// own swatch colour (data-hex) overrides these.
export const COLOURS = {
  brown: '#6b4a33', green: '#3f4f3c', blue: '#2f4858', grey: '#54534f', black: '#232321',
  red: '#b8131b', cream: '#ddd5c4', teal: '#1d5b5b', pink: '#bc3e90', white: '#ffffff',
};
const PALE = /^(cream|white|ivory|yellow)$/;


export function colourKey(value) {
  const v = String(value || '').toLowerCase();
  if (/grey|gray|storm/.test(v)) return 'grey';
  if (/rose|pink/.test(v)) return 'pink';
  return v.trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function sizeKey(value) {
  const v = String(value || '');
  if (/small/i.test(v)) return 'Small';
  if (/medium/i.test(v)) return 'Medium';
  if (/large/i.test(v)) return 'Large';
  const m = v.match(/(\d+(?:\.\d+)?)\s*(?:cm)?[^\d]+?(\d+(?:\.\d+)?)/);
  if (m) {
    const big = Math.max(parseFloat(m[1]), parseFloat(m[2]));
    return big <= 60 ? 'Small' : big <= 80 ? 'Medium' : 'Large';
  }
  return 'Large';
}

export const orientKey = (v) => (/land/i.test(String(v || '')) ? 'Landscape' : 'Portrait');

// ---- measuring: a hidden span carrying the identical face, weight and tracking ------------
let span = null;
const cache = new Map();
export function measure(str) {
  if (!str) return 0;
  if (cache.has(str)) return cache.get(str);
  if (span && !span.isConnected) span = null;
  if (!span) {
    span = document.createElement('span');
    span.setAttribute('style', 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre;font-family:' + FACE + ';font-weight:600;font-size:100px;letter-spacing:1px;');
    document.body.appendChild(span);
  }
  span.textContent = str;
  const w = span.offsetWidth;
  if (w > 0) cache.set(str, w);
  return w;
}

// The master: artboard and the vertical gaps between rows, reused when re-stacking
const M = { w: 2154.3, h: 2863, cx: 1077.15, gapCrown: 148.3, gapTight: 82, gapWord: 90 };

// The six groups, verbatim, with their getBBox() boxes [x1, y1, x2, y2]
const G = {
  crown: { box: [822.6, 263.9, 1329.1, 731], svg: `<circle cx="1027.8" cy="371" r="12.9"></circle><path d="M1007.2,379.5c0,6.8-5.5,12.2-12.2,12.2s-12.2-5.5-12.2-12.2,5.5-12.2,12.2-12.2,12.2,5.5,12.2,12.2Z"></path><path d="M975.6,391.6c0,6.3-5.1,11.4-11.4,11.4s-11.4-5.1-11.4-11.4,5.1-11.4,11.4-11.4,11.4,5.1,11.4,11.4Z"></path><path d="M947.7,405.1c0,5.8-4.7,10.5-10.5,10.5s-10.5-4.7-10.5-10.5,4.7-10.5,10.5-10.5,10.5,4.7,10.5,10.5Z"></path><path d="M919.9,422.5c0,5.3-4.3,9.6-9.6,9.6s-9.6-4.3-9.6-9.6,4.3-9.6,9.6-9.6,9.6,4.3,9.6,9.6Z"></path><path d="M895.2,440.6c0,4.9-4,8.9-8.9,8.9s-8.9-4-8.9-8.9,4-8.9,8.9-8.9,8.9,4,8.9,8.9Z"></path><path d="M875.7,459.5c0,4.5-3.6,8.1-8.1,8.1s-8.1-3.6-8.1-8.1,3.6-8.1,8.1-8.1,8.1,3.6,8.1,8.1Z"></path><path d="M881.2,481.4h0s0,0,0,0h0ZM872.7,472.2c.2-.2,20.3-18.9,51-38.9h0c30.7-19.9,71.9-41.2,114.8-45.8h0l3.9-.4,2.1,3.4c.3.5,15.8,25.7,17.5,62.8h0l-12.4.6c-1.2-25.9-9.9-45.7-13.8-53.4h0c-35.8,4.8-71.5,22.2-99.5,39.8h0c-29.1,18.2-49.8,36.5-54.2,40.4h0c-.6.5-.8.8-.8.8h0l-8.5-9.1h0Z"></path><circle cx="1124.5" cy="371" r="12.9"></circle><path d="M1145.1,379.5c0,6.8,5.5,12.2,12.2,12.2s12.2-5.5,12.2-12.2-5.5-12.2-12.2-12.2-12.2,5.5-12.2,12.2Z"></path><path d="M1176.7,391.6c0,6.3,5.1,11.4,11.4,11.4s11.4-5.1,11.4-11.4-5.1-11.4-11.4-11.4-11.4,5.1-11.4,11.4Z"></path><path d="M1204.6,405.1c0,5.8,4.7,10.5,10.5,10.5s10.5-4.7,10.5-10.5-4.7-10.5-10.5-10.5-10.5,4.7-10.5,10.5Z"></path><path d="M1232.4,422.5c0,5.3,4.3,9.6,9.6,9.6s9.6-4.3,9.6-9.6-4.3-9.6-9.6-9.6-9.6,4.3-9.6,9.6Z"></path><path d="M1257.1,440.6c0,4.9,4,8.9,8.9,8.9s8.9-4,8.9-8.9-4-8.9-8.9-8.9-8.9,4-8.9,8.9Z"></path><path d="M1276.5,459.5c0,4.5,3.6,8.1,8.1,8.1s8.1-3.6,8.1-8.1-3.6-8.1-8.1-8.1-8.1,3.6-8.1,8.1Z"></path><path d="M1271.2,481.4h0s0,0,0,0h0ZM1279.7,472.2l-8.5,9.1h0s-.3-.2-.9-.8h0c-4.3-3.9-25.1-22.2-54.2-40.4h0c-28-17.6-63.7-35-99.5-39.8h0c-3.9,7.7-12.6,27.5-13.8,53.4l-12.4-.6h0c1.8-37.1,17.2-62.2,17.5-62.8l2.1-3.4,3.9.4h0c42.9,4.6,84.1,25.9,114.8,45.8h0c30.7,20,50.9,38.7,51,38.9h0Z"></path><path d="M1082.6,325.6v-16.4l20.8,9.2v-37l-21.1,11.7,13.6-29.2h-39l13.8,29.5-21.6-12v37l20.8-9.2v16.4c-12.1,2.9-21.1,13.8-21.1,26.8s12.3,27.5,27.5,27.5,27.5-12.3,27.5-27.5-9-23.9-21.1-26.8ZM1069.8,333.7v15l-12.2-7.5,12.2-7.5ZM1076.8,372.9c-8.5,0-16-5.2-16-7.1s6.9-3.3,15.4-3.3,15.4,1.5,15.4,3.3-6.3,7.1-14.7,7.1ZM1082.5,348.7v-15l12.2,7.5-12.2,7.5Z"></path><path d="M1087.5,398.2c0,6.3-5.1,11.4-11.4,11.4s-11.4-5.1-11.4-11.4,5.1-11.4,11.4-11.4,11.4,5.1,11.4,11.4Z"></path><path d="M1085.8,425.3c0,5.3-4.3,9.6-9.6,9.6s-9.6-4.3-9.6-9.6,4.3-9.6,9.6-9.6,9.6,4.3,9.6,9.6Z"></path><path d="M1084.2,449.4c0,4.5-3.6,8.1-8.1,8.1s-8.1-3.6-8.1-8.1,3.6-8.1,8.1-8.1,8.1,3.6,8.1,8.1Z"></path><path d="M1329.1,495.6l-42.1-10.8,16.3,36.6-24.2-17.7-13.2,63.8,30.4-18.2s-14.8,52.4-53.6,51.6c-47.6-1-56.9-58.8-22.2-62.9,0,0,22.2-2.4,20.3,38.3,0,0,22.5-7.9,22.5-38.5s-34-37.8-34-37.8c0,0-14.8-.7-20.8,4.8,0,0,18.4-35.6-5.3-68.9,0,0-33.2,19.4-26.8,65,0,0-9.8-11-26.1-11s-26.3,19.4-26.3,33,15.1,29.2,21,31.6c0,0-3.7-25.8,14.6-26.3,17-.5,19.1,23.4,19.1,23.4,0,0,1.7,34.2-45,34.4-41.8.2-45.9-48.3-45.9-48.3l25.3,13.6v-63l-25.8,18.8,12.4-44.5h-47.8l12.4,44.5-25.8-18.8v63l25.3-13.6s-4.1,48.5-45.9,48.3c-46.6-.3-45-34.4-45-34.4,0,0,2.2-23.9,19.1-23.4,18.2.5,14.6,26.3,14.6,26.3,6-2.4,21-17.9,21-31.6s-10-33-26.3-33-26.1,11-26.1,11c6.5-45.7-26.8-65-26.8-65-23.7,33.2-5.3,68.9-5.3,68.9-6-5.5-20.8-4.8-20.8-4.8,0,0-34,1-34,37.8s22.5,38.5,22.5,38.5c-1.9-40.7,20.3-38.3,20.3-38.3,34.7,4.1,25.3,61.9-22.2,62.9-38.8.8-53.6-51.6-53.6-51.6l30.4,18.2-13.2-63.8-24.1,17.7,16.3-36.6-42.1,10.8s11.9,24.9,8.4,33.7c-5.5,13.6-2.6,20-.5,25.1,2.6,6.5,11.5,12.4,12.4,14.3,1,1.9,8.1,25.6,10.8,43.3,2.6,17.7-4.5,45.3-4.5,45.3l34.9,32.6c0-3.2,80.3-34,191.3-31.8h.4c110.9-2.2,191.3,28.6,191.3,31.8l34.9-32.6s-7.1-27.6-4.5-45.3c2.6-17.7,9.8-41.4,10.8-43.3,1-1.9,9.8-7.9,12.4-14.3,2.1-5.2,5-11.5-.5-25.1-3.6-8.8,8.4-33.7,8.4-33.7ZM843.1,555.1l-8.4-7,3.9-10.2,7.6,7.2-3.1,10ZM874.2,663.1l-12.1-11.1,5-15,11.1,10.6-4,15.5ZM903.1,627.6c0-3.8,3.1-6.9,6.9-6.9s6.9,3.1,6.9,6.9-3.1,6.9-6.9,6.9-6.9-3.1-6.9-6.9ZM914.2,661.8c-3.8,0-6.8-3.1-6.8-6.9s3.1-6.9,6.8-6.9,6.9,3.1,6.9,6.9-3.1,6.9-6.9,6.9ZM973.2,645.5c-16,1.5-29.9-6.8-30.9-18.4-1.1-11.7,11.1-22.3,27.1-23.8,16-1.5,29.9,6.8,30.9,18.4,1.1,11.6-11,22.3-27.1,23.8ZM1017.6,607.7c.2-3.8,3.4-6.7,7.2-6.5,3.8.2,6.7,3.4,6.5,7.2-.2,3.8-3.4,6.7-7.2,6.5-3.8-.2-6.7-3.4-6.5-7.2ZM1034.2,636c-.2,3.8-3.4,6.7-7.2,6.5-3.8-.2-6.7-3.4-6.5-7.2.2-3.8,3.4-6.7,7.2-6.5,3.8.2,6.7,3.4,6.5,7.2ZM1076.1,516.7c2.8,0,5,2.2,5,5s-2.2,5-5,5-5-2.2-5-5,2.2-5,5-5ZM1075.7,636.5l-11.8-19,11.8-19,12.6,19-12.6,19ZM1125.3,642.5c-3.8.2-7-2.7-7.2-6.5-.2-3.8,2.7-7,6.5-7.2,3.8-.2,7,2.7,7.2,6.5.2,3.8-2.7,7-6.5,7.2ZM1128.2,614.9c-3.8.2-7-2.7-7.2-6.5-.2-3.8,2.7-7,6.5-7.2,3.8-.2,7,2.7,7.2,6.5.2,3.8-2.7,7-6.5,7.2ZM1210,627.1c-1.1,11.6-14.9,19.9-30.9,18.4-16-1.5-28.1-12.1-27.1-23.8,1.1-11.7,14.9-19.9,30.9-18.4,16,1.5,28.1,12.1,27.1,23.8ZM1238,661.8c-3.8,0-6.9-3.1-6.9-6.9s3.1-6.9,6.9-6.9,6.9,3.1,6.9,6.9-3.1,6.9-6.9,6.9ZM1242.3,634.4c-3.8,0-6.8-3.1-6.8-6.9s3.1-6.9,6.8-6.9,6.9,3.1,6.9,6.9-3.1,6.9-6.9,6.9ZM1278.1,663.1l-4-15.5,11.1-10.6,5,15-12.1,11.1ZM1309.1,555.1l-3.1-10,7.6-7.2,3.9,10.2-8.4,7Z"></path><path d="M1257,700c0,17.1-80.9,31-180.8,31s-180.8-13.9-180.8-31,80.9-31,180.8-31,180.8,13.9,180.8,31Z"></path>` },
  KEEP: { box: [650.6, 879.3, 1531.4, 1141.1], svg: `<polygon points="751.3 1009.7 861.3 879.4 803.8 879.4 700.5 997.1 700.5 879.5 650.6 879.5 650.6 1141 700.5 1141 700.5 1032.2 800.3 1141.1 868.3 1141.1 751.3 1009.7"></polygon><polygon points="946.7 1102.1 946.7 1024.2 1068.3 1024.2 1068.3 985.7 946.7 985.7 946.7 917.8 1068.3 917.8 1068.3 879.5 896.8 879.5 896.8 1141 1068.5 1141 1068.5 1101.9 946.7 1102.1"></polygon><polygon points="1172.8 1102.1 1172.8 1024.2 1294.5 1024.2 1294.5 985.7 1172.8 985.7 1172.8 917.8 1294.5 917.8 1294.5 879.5 1123 879.5 1123 1141 1294.7 1141 1294.7 1101.9 1172.8 1102.1"></polygon><path d="M1531.4,963.1c1-59.6-48.9-81.2-80.3-83.5-7.9-.6-41.7,0-47.4,0h0c0-.1-52.5-.1-52.5-.1v261.5h52.5v-85.1c6.9,0,39.4.4,47.4-.2,24.3-1.8,81.6-19.9,80.3-92.6ZM1432.4,1017.5c-4.4.4-24.4,0-28.7,0v-102.6c4,0,22,.2,29.1.2s49.3,8.9,48.4,48.8c-1,47-44,53.2-48.8,53.6Z"></path>` },
  CALM: { box: [559.6, 1226.2, 1592.8, 1494.8], svg: `<path d="M1033.1,1489.8h58.8l-118.5-255h-43.2l-118.5,255h58.8l22.9-51.8h116.7l22.9,51.8ZM912.7,1394.5l39.1-88.4,39.1,88.4h-78.2Z"></path><polygon points="1182.6 1442.8 1302.3 1442.8 1301.9 1489.6 1126.9 1489.8 1126.9 1234.8 1182.6 1234.8 1182.6 1442.8"></polygon><polygon points="1534.6 1234.9 1592.8 1234.9 1592.8 1489.9 1541.7 1489.9 1541.7 1325.3 1467.1 1457.1 1391.1 1325.3 1391.1 1489.9 1340 1489.9 1340 1234.9 1399 1234.9 1466.3 1356.1 1534.6 1234.9"></polygon><path d="M772.7,1408.7c-15,21.3-41,35.5-70.4,35.5-46.4,0-89.1-33.2-89.1-83.9s42.8-83.9,89.1-83.9,55.3,14.8,70.9,37.2v-1.4s37.8-35.2,37.8-35.2c-25-28.9-63.8-48.4-103.8-50.6-76-4.1-147.6,54.5-147.6,132.5,0,74.5,64.9,135.9,138.8,135.9,42.3,0,84.9-16.3,111.8-50.4l-37.4-35.7Z"></path>` },
  AND: { box: [885.6, 1574.9, 1266.6, 1697.7], svg: `<path d="M985.8,1697.2h25.7l-53.5-120.8h-18.9l-53.5,120.8h25.7l11.7-24.6h51l11.7,24.6ZM931.5,1651.9l17.1-41.3,17.1,41.3h-34.2Z"></path><polygon points="1103.3 1575.1 1103.3 1651.7 1052.7 1575.1 1028.2 1575.1 1028.2 1697.7 1052.7 1697.7 1052.7 1618.1 1103.7 1697.7 1126.2 1697.7 1126.2 1575.1 1103.3 1575.1"></polygon><path d="M1216.7,1576c-7.8-1.6-58.5-.9-58.5-.9v122.5s53.8.2,58.5-.2c16.4-1.4,50-20.1,49.9-60.9v-2c-.2-38.3-30.8-54.6-49.9-58.4ZM1235.7,1634.5v2c-.1,25-21,32.7-29.3,33.8-3.6.4-11.7.6-19,.6v-70.2c7.1,0,19.8,0,21.5.1,2.4.1,28.2,4.8,26.8,33.7Z"></path>` },
  CARRY: { box: [475.4, 1790.5, 1676.9, 2065.7], svg: `<path d="M731,1846c-24.6-33.7-67.2-53.1-107.3-55.3-76.3-4.2-148.3,55.9-148.3,135.8,0,76.3,65.2,138.8,139.4,139.2,40.8.2,81.5-13.8,110.6-47.4l-36.4-38.1c-13.1,17.9-41.3,34-70.9,34-46.6,0-88.9-34.3-88.9-86.3s43-86,89.5-86,58,16.6,73.6,39.6l38.6-35.5Z"></path><path d="M942.5,2059.2h57.1l-115.1-261.6h-42l-115.1,261.6h57.1l22.3-51.8h113.4l22.3,51.8ZM825.6,1962.3l38-90,38,90h-76Z"></path><path d="M1204.9,2047c-11.5-32.4-20.2-60.2-28.7-78.6-9.2-20-23.3-23.7-27.6-25.8.3-.2.7-.3,1-.5,12.3-5.8,43.1-27.6,42.5-64.2,1-61.9-42.7-78.2-74.1-80.5-7.9-.6-41.7,0-47.4,0h0c0-.1-52.5-.1-52.5-.1v261.5h50.9v-102c4.9,0,23.9.2,37.2,0,20.7,11.4,25.1,32.8,33.1,53.4,4.7,12.2,10.8,31.1,17.2,48.9h53.6c-1.3-3.1-3.3-7-5.2-12.2ZM1107.5,1912.7c-19.2,0-37.3.3-37.6.3l-.3-74.9c1,0,28,0,37.9,0,19.3,0,34.9,18.1,34.9,37.4s-15.6,37.4-34.9,37.4Z"></path><path d="M1421.7,2047c-11.5-32.4-20.2-60.2-28.7-78.6-9.2-20-23.4-23.7-27.6-25.8.3-.2.7-.3,1-.5,12.3-5.8,43.1-27.6,42.5-64.2,1-61.9-42.7-78.2-74.1-80.5-7.9-.6-41.7,0-47.4,0h0c0-.1-52.5-.1-52.5-.1v261.5h50.9v-102c4.9,0,23.9.2,37.2,0,20.7,11.4,25.1,32.8,33.1,53.4,4.7,12.2,10.8,31.1,17.2,48.9h53.6c-1.3-3.1-3.3-7-5.2-12.2ZM1324.3,1912.7c-19.2,0-37.3.3-37.6.3l-.3-74.9c1,0,28,0,37.9,0,19.3,0,34.9,18.1,34.9,37.4s-15.6,37.4-34.9,37.4Z"></path><polygon points="1676.9 1797 1614.7 1797 1545 1909.4 1475.2 1797 1413 1797 1518.2 1953 1518.2 2059.2 1571.7 2059.2 1571.7 1953 1676.9 1797"></polygon>` },
  ON: { box: [805.6, 2148.7, 1346.6, 2429.7], svg: `<path d="M945.5,2148.7c-77.3,0-139.9,62.9-139.9,140.5s62.6,140.5,139.9,140.5,139.9-62.9,139.9-140.5-62.6-140.5-139.9-140.5ZM946.1,2376.7c-48.7,0-88.6-38.6-88.6-87.5s39.6-87.5,88.3-87.5,87.9,38.6,87.9,87.5-38.9,87.5-87.6,87.5Z"></path><polygon points="1295.7 2154.3 1295.7 2321.2 1183.3 2154.3 1128.9 2154.3 1128.9 2421.5 1183.3 2421.5 1183.3 2248.1 1296.5 2421.5 1346.6 2421.5 1346.6 2154.3 1295.7 2154.3"></polygon>` },
};
const ORDER = ['crown', 'KEEP', 'CALM', 'AND', 'CARRY', 'ON'];
const wOf = (g) => g.box[2] - g.box[0];
const hOf = (g) => g.box[3] - g.box[1];

/**
 * Lays the poster out on a board of cm[0] × cm[1], in millimetres. Returns the placement
 * (translate + uniform scale) of each group and the added word's row, if any.
 */
export function layout(cm, extra) {
  const W = cm[0] * 10, H = cm[1] * 10;
  const portrait = H >= W;
  const word = String(extra || '').trim();

  // the genuine poster, untouched
  if (portrait && !word) {
    const k = Math.min(W / M.w, H / M.h);
    const ox = (W - M.w * k) / 2, oy = (H - M.h * k) / 2;
    const tr = 'translate(' + ox.toFixed(2) + ' ' + oy.toFixed(2) + ') scale(' + k.toFixed(5) + ')';
    return { W, H, portrait, classic: true, min: W * 0.075, measure: W * 0.86, rows: [], place: ORDER.map((key) => ({ key, t: tr })), tight: false };
  }

  // otherwise the rows are re-stacked: portrait keeps four lines with CARRY ON joined, landscape goes to three
  const lines = portrait ? [['KEEP'], ['CALM'], ['AND'], ['CARRY', 'ON']] : [['KEEP', 'CALM'], ['AND'], ['CARRY', 'ON']];
  const gaps = portrait ? [M.gapCrown, 85.1, 80.1, 92.9] : [M.gapCrown, M.gapTight, M.gapWord];
  const space = hOf(G.CALM) * 0.3;
  const lineW = lines.map((ws) => ws.reduce((a, w, i) => a + wOf(G[w]) + (i ? space : 0), 0));
  const lineH = lines.map((ws) => Math.max.apply(null, ws.map((w) => hOf(G[w]))));
  const blockW = Math.max.apply(null, lineW);
  const blockH = hOf(G.crown) + gaps.reduce((a, g) => a + g, 0) + lineH.reduce((a, h) => a + h, 0);

  const kFit = Math.min((W * 0.86) / blockW, (H * (portrait ? 0.8 : 0.74)) / blockH);
  const meas = blockW * kFit;
  const minFs = W * (portrait ? 0.075 : 0.05);
  const fs = word ? Math.min(H * (portrait ? 0.105 : 0.125), (() => { const at = measure(word); return at ? (100 * meas) / at : H * 0.105; })()) : 0;
  const gap = word ? fs * 0.34 : 0;
  const wordsH = word ? gap + fs * 0.78 : 0;
  const k = Math.min(kFit, (H * 0.92 - wordsH) / blockH);

  const cx = W / 2;
  const oy = Math.max(0, (H - (blockH * k + wordsH)) / 2);
  const place = [{ key: 'crown', t: 'translate(' + (cx - (G.crown.box[0] + wOf(G.crown) / 2) * k).toFixed(2) + ' ' + (oy - G.crown.box[1] * k).toFixed(2) + ') scale(' + k.toFixed(5) + ')' }];
  let y = oy + hOf(G.crown) * k;
  lines.forEach((ws, li) => {
    y += gaps[li] * k;
    let x = cx - (lineW[li] * k) / 2;
    const base = y + lineH[li] * k;
    ws.forEach((w) => {
      const g = G[w];
      // every group sits on its line's own foot, so a shorter row is not left floating
      place.push({ key: w, t: 'translate(' + (x - g.box[0] * k).toFixed(2) + ' ' + (base - g.box[3] * k).toFixed(2) + ') scale(' + k.toFixed(5) + ')' });
      x += (wOf(g) + space) * k;
    });
    y = base;
  });
  return {
    W, H, portrait, classic: false, measure: meas, min: minFs, place,
    rows: word ? [{ s: word, fs, x: cx, y: y + gap + fs * 0.78 }] : [],
    tight: word ? fs < minFs * 1.02 : false,
  };
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** One board face as SVG markup: the ground, the six groups placed, the added word. */
export function face(cm, extra, colour, hex) {
  const L = layout(cm, extra);
  const key = colourKey(colour) || 'red';
  const ground = hex || COLOURS[key] || COLOURS.red;
  const ink = PALE.test(key) ? DARK_INK : INK;
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + L.W + ' ' + L.H + '" preserveAspectRatio="none" style="display:block;width:100%;height:100%">' +
    '<rect x="0" y="0" width="' + L.W + '" height="' + L.H + '" fill="' + ground + '"></rect>' +
    L.place.map((p) => '<g fill="' + ink + '" transform="' + p.t + '">' + G[p.key].svg + '</g>').join('') +
    L.rows.map((r) => '<text x="' + r.x.toFixed(2) + '" y="' + r.y.toFixed(2) + '" text-anchor="middle" style="font-family:' + FACE.replace(/"/g, '&quot;') + ';font-weight:600;font-size:' + r.fs.toFixed(2) + 'px;letter-spacing:' + (r.fs * 0.01).toFixed(2) + 'px;fill:' + ink + '">' + esc(r.s) + '</text>').join('') +
    '</svg>';
}

let assetMap = null;
function assets() {
  if (assetMap) return assetMap;
  const el = document.querySelector('script[data-btf-planner-assets]');
  try { assetMap = el ? JSON.parse(el.textContent) : {}; } catch (e) { assetMap = {}; }
  return assetMap;
}


/**
 * One board.
 * data-orient  Landscape | Portrait     data-size  Small | Medium | Large (or the store's option value)
 * data-view    product | room           data-cap   white | black       data-colour  colourway key
 * data-hex     ground override          data-word  the added word      data-layer  full (default) | face
 * data-max     cap the stage width (px); omit to fill the container
 */
class KeepCalmBoard extends HTMLElement {
  static get observedAttributes() {
    return ['data-orient', 'data-size', 'data-view', 'data-cap', 'data-colour', 'data-hex', 'data-word', 'data-layer', 'data-max'];
  }
  #raf = 0;
  #built = false;
  #stage; #art; #area; #caps;
  #want = '';

  connectedCallback() { this.#build(); this.#schedule(); }
  attributeChangedCallback() { if (this.#built) this.#schedule(); }

  #build() {
    if (this.#built) return;
    this.#built = true;
    this.innerHTML = '<span class="btf-pb__stage"><span class="btf-pb__art"></span><span class="btf-pb__area"></span><span class="btf-pb__caps"></span></span>';
    this.#stage = this.firstElementChild;
    [this.#art, this.#area, this.#caps] = Array.from(this.#stage.children);
  }

  #schedule() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => { this.#raf = 0; this.#render(); });
  }

  #render() {
    const orient = orientKey(this.dataset.orient);
    const size = sizeKey(this.dataset.size);
    const room = this.dataset.view === 'room';
    const cap = /black/i.test(this.dataset.cap || '') ? 'black' : 'white';
    const cm = CM[orient][size];
    const radius = (350 / cm[0]).toFixed(2) + '% / ' + (350 / cm[1]).toFixed(2) + '%';
    const faceOnly = this.dataset.layer === 'face';

    if (faceOnly) {
      // The face alone, at the board's own proportions and 35 mm corner radius. The radius goes
      // on the host too, so a selected outline follows the board's corners.
      this.style.cssText = 'display:block;width:100%;border-radius:' + radius + ';';
      this.#stage.style.cssText = 'display:block;position:relative;width:100%;aspect-ratio:' + cm[0] + ' / ' + cm[1] + ';overflow:hidden;border-radius:' + radius + ';';
      this.#art.style.cssText = 'display:block;position:absolute;inset:0;';
      this.#area.style.cssText = this.#caps.style.cssText = 'display:none;';
    } else {
      const pr = PROD[orient];
      const r = room ? ROOM[orient][size] : pr[size];
      const plate = (orient === 'Landscape' ? 'land' : 'port') + '-' + PLATE[size];
      const map = assets();
      const base = map[(room ? 'room-' : 'product-') + plate + (room ? '-room' : '-area')] || '';
      const caps = map[(room ? 'room-' : 'product-') + plate + '-caps-' + cap] || '';
      const max = parseInt(this.dataset.max || '', 10);
      const width = max ? 'min(100%, ' + Math.round(max * (room ? 1 : pr.arNum)) + 'px)' : '100%';
      this.#stage.style.cssText = 'display:block;position:relative;width:' + width + ';height:auto;aspect-ratio:' + (room ? '1 / 1' : pr.ar) + ';background:#fff;isolation:isolate;margin:0 auto;';
      this.#art.style.cssText = 'display:block;position:absolute;left:' + r[0] + '%;top:' + r[1] + '%;width:' + r[2] + '%;height:' + (r[3] + (room ? 0 : 0.5)) + '%;z-index:1;overflow:hidden;border-radius:' + radius + ';';
      this.#area.style.cssText = 'display:block;position:absolute;inset:0;z-index:2;pointer-events:none;mix-blend-mode:multiply;background-image:url("' + base + '");background-size:100% 100%;background-repeat:no-repeat;';
      this.#caps.style.cssText = 'display:block;position:absolute;inset:0;z-index:3;pointer-events:none;background-image:url("' + caps + '");background-size:100% 100%;background-repeat:no-repeat;';
    }

    const want = [orient, size, this.dataset.colour, this.dataset.hex, this.dataset.word].join('\u0001');
    if (want === this.#want) return;
    this.#want = want;
    this.#art.innerHTML = face(cm, this.dataset.word || '', this.dataset.colour, this.dataset.hex);
    this.dataset.ready = 'true';
  }
}

const FILTER = /[^A-Z' \-&]/g;
const STORE_KEY = 'btf-keepcalm-state';
const SAMPLE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The design params a page was opened with (a shared link, assets/btf-share.js), or null when
 * there are none or a basket line is being edited, which btf-edit-line.js restores instead.
 */
function sharedDesign(keys) {
  const p = new URLSearchParams(window.location.search);
  if (p.has('edit') || (window.btfEditLine && window.btfEditLine.active)) return null;
  return keys.some((k) => p.has(k)) ? p : null;
}

/**
 * The product-page controller. Lives in the preview block and reaches the word field, the
 * format and cap toggles, the colourway links and Horizon's variant picker through the section,
 * so Horizon's price and buy button keep doing their jobs while every board follows along.
 * Colourways are sibling products rather than a variant option, so a swatch is a link: it
 * stores the customer's word, format and caps in sessionStorage and the sibling page restores
 * them (the size travels in the link's own ?variant=).
 */
class KeepCalmPreview extends HTMLElement {
  #ready = false;
  #landAt = null;
  #state = { orient: 'Portrait', cap: 'white', view: 'product' };

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.section = this.closest('.shopify-section') || document.body;
    this.board = this.querySelector('btf-keepcalm-board');
    this.variants = this.#json(this.section.querySelector('script[data-btf-variants]')) || { names: [], list: [] };
    this.picker = this.section.querySelector('variant-picker');
    this.wordInput = this.section.querySelector('[data-btf-word]');
    const orientRadio = this.section.querySelector('[data-btf-orient]:checked');
    const capRadio = this.section.querySelector('[data-btf-cap]:checked');
    this.#state.orient = orientKey(orientRadio ? orientRadio.value : this.board && this.board.dataset.orient);
    this.#state.cap = /black/i.test((capRadio ? capRadio.value : (this.board && this.board.dataset.cap)) || '')
      ? 'black'
      : 'white';

    this.section.addEventListener('input', (e) => {
      if (e.target === this.wordInput) this.#text();
    });
    // Format and screw caps are line-item properties on the product form, not variant options,
    // so they are ordinary radios: the form carries them and the boards follow the change.
    this.section.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.matches('[data-btf-orient]')) this.#setOrient(t.value);
      else if (t.matches('[data-btf-cap]')) this.#setCap(t.value);
      else if (this.picker && this.picker.contains(t)) this.#variant();
    });
    this.section.addEventListener('click', (e) => {
      const t = e.target.closest && e.target.closest('[data-btf-view], [data-btf-swatch]');
      if (!t || !this.section.contains(t)) return;
      if (t.hasAttribute('data-btf-swatch')) {
        this.#store(t);
      } else if (t.dataset.btfView) {
        e.preventDefault();
        this.#setView(t.dataset.btfView);
      }
    });
    if (this.wordInput) this.wordInput.addEventListener('focus', () => this.wordInput.select());

    this.#restore();
    // A shared link (assets/btf-share.js) carries the variant, which Shopify selects, and the
    // words, format and caps, which override anything restored above
    const shared = sharedDesign(['word', 'orient', 'caps']);
    if (shared) {
      if (this.wordInput && shared.has('word')) this.wordInput.value = shared.get('word');
      if (shared.has('orient')) this.#state.orient = orientKey(shared.get('orient'));
      if (shared.has('caps')) this.#state.cap = shared.get('caps') === 'black' ? 'black' : 'white';
    }
    this.#setOrient(this.#state.orient, true);
    this.#setCap(this.#state.cap);
    this.#setView(this.#state.view);
    this.#variant();
    this.#share(Boolean(shared));
    if (this.#landAt !== null) this.#land(this.#landAt);
  }

  // "Send it to someone before you decide": shown once the design is the customer's own
  #share(open) {
    const share = this.querySelector('btf-share');
    if (!share) return;
    share.provider = {
      params: () => {
        const p = new URLSearchParams();
        if (this.variantId) p.set('variant', String(this.variantId));
        if (this.wordInput && this.wordInput.value) p.set('word', this.wordInput.value);
        p.set('orient', this.#state.orient.toLowerCase());
        p.set('caps', this.#state.cap);
        return p;
      },
      tokens: () => {
        const w = this.wordInput ? this.wordInput.value : '';
        return {
          title: this.dataset.title || '',
          words: w ? ', “' + w + '”' : '',
          size: this.#cm().join(' × ') + ' cm · ' + this.#state.orient.toLowerCase(),
          price: this.variantPrice || '',
        };
      },
    };
    const show = () => (share.hidden = false);
    if (open) show();
    else {
      this.section.addEventListener('input', (e) => e.isTrusted && show());
      this.section.addEventListener('change', (e) => e.isTrusted && show());
      this.section.addEventListener('click', (e) => e.isTrusted && e.target instanceof Element && e.target.closest('[data-btf-swatch]') && show());
    }
  }

  #json(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // the word and the two property choices travel with the customer between colourway siblings
  #store(swatch) {
    // where the colour row sits on screen, so the sibling page can open with it in the same place
    const row = swatch && (swatch.closest('.btf-picker__group--colourways') || swatch);
    try {
      sessionStorage.setItem(
        STORE_KEY,
        JSON.stringify({
          y: row ? Math.round(row.getBoundingClientRect().top) : null,
          word: this.wordInput ? this.wordInput.value : '',
          orient: this.#state.orient,
          cap: this.#state.cap,
          view: this.#state.view,
          t: Date.now(),
        })
      );
    } catch (e) {
      /* storage unavailable: the sibling simply starts from its own defaults */
    }
  }

  #restore() {
    let s = null;
    try {
      s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      sessionStorage.removeItem(STORE_KEY);
    } catch (e) {
      s = null;
    }
    if (!s || Date.now() - (s.t || 0) > 600000) return;
    if (this.wordInput && typeof s.word === 'string') this.wordInput.value = s.word;
    if (s.orient) this.#state.orient = orientKey(s.orient);
    if (s.cap) this.#state.cap = s.cap === 'black' ? 'black' : 'white';
    if (s.view) this.#state.view = s.view === 'room' ? 'room' : 'product';
    if (typeof s.y === 'number') this.#landAt = s.y;
  }

  // A colourway is its own page, which a browser opens at the top. Opened from a swatch, this
  // page scrolls so its colour row sits where the customer clicked it, and settles again once
  // the pictures above have loaded, unless the customer has started scrolling by then.
  #land(y) {
    const row = this.section.querySelector('.btf-picker__group--colourways');
    if (!row) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    let moved = false;
    const stop = () => (moved = true);
    ['wheel', 'touchstart', 'keydown'].forEach((ev) => window.addEventListener(ev, stop, { once: true, passive: true }));
    // Horizon scrolls the page inside .page-wrapper on desktop, and the window on a phone
    const go = () => {
      if (moved) return;
      const wrap = document.querySelector('.page-wrapper');
      const inner = wrap && /auto|scroll/.test(getComputedStyle(wrap).overflowY) && wrap.scrollHeight > wrap.clientHeight;
      const by = row.getBoundingClientRect().top - y;
      if (inner) wrap.scrollTo({ top: wrap.scrollTop + by, behavior: 'instant' });
      else window.scrollTo({ top: window.scrollY + by, behavior: 'instant' });
    };
    go();
    requestAnimationFrame(go);
    if (document.readyState === 'complete') setTimeout(go, 0);
    else window.addEventListener('load', go, { once: true });
  }

  #selected() {
    const out = {};
    if (!this.picker) return out;
    this.picker.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
      if (i.dataset.optionName) out[i.dataset.optionName] = i.value;
    });
    this.picker.querySelectorAll('select').forEach((s) => {
      const o = s.selectedOptions[0];
      if (o && o.dataset.optionName) out[o.dataset.optionName] = o.value;
    });
    return out;
  }

  #sizeValue() {
    const sel = this.#selected();
    const k = Object.keys(sel).find((key) => /size/i.test(key));
    return k ? sel[k] : '';
  }

  #cm() {
    return CM[this.#state.orient][sizeKey(this.#sizeValue())];
  }

  #boards() {
    return Array.from(this.section.querySelectorAll('btf-keepcalm-board'));
  }

  #text() {
    const cm = this.#cm();
    // The limit is the measure the freed row actually carries at the smallest word size the
    // design allows — never at the fitted size, which would depend on the text, which depends
    // on the limit.
    const L = layout(cm, 'A');
    const perChar = (measure(SAMPLE) / SAMPLE.length) * (L.min / 100);
    const max = perChar ? Math.max(14, Math.floor(L.measure / perChar)) : 16;
    let word = '';
    if (this.wordInput) {
      word = this.wordInput.value.toUpperCase().replace(FILTER, '').slice(0, max);
      if (word !== this.wordInput.value) this.wordInput.value = word;
      this.wordInput.setAttribute('maxlength', max);
      // The word is optional: an empty field must not put a blank "Added word" on the order,
      // so the field only carries its name while it has something to say.
      const name = this.wordInput.dataset.btfName || this.wordInput.getAttribute('name');
      if (name) this.wordInput.dataset.btfName = name;
      if (word) this.wordInput.setAttribute('name', this.wordInput.dataset.btfName || '');
      else this.wordInput.removeAttribute('name');
    }
    this.section.querySelectorAll('[data-btf-count="word"]').forEach((el) => {
      el.textContent = word.length + ' / ' + max;
    });
    const T = layout(cm, word);
    this.section.querySelectorAll('[data-btf-tight]').forEach((el) => {
      el.hidden = !T.tight;
    });
    this.#boards().forEach((b) => {
      b.dataset.word = word;
    });
  }

  // The picker opened with no size chosen (blocks/btf-product-picker.liquid) and none is ticked
  // yet: the preview stays on the large board and the price reads "From £75"
  #sizeOpen() {
    const g = this.section.querySelector('.btf-picker__group--size');
    return !!(g && g.querySelector('[data-btf-size-prompt]') && !g.querySelector('input:checked'));
  }

  #variant() {
    const sel = this.#selected();
    const names = this.variants.names || [];
    const list = this.variants.list || [];
    const variant = list.find((v) => names.every((n, i) => sel[n] == null || v.options[i] === sel[n])) || list[0];
    this.variantId = variant ? variant.id : null;
    this.variantPrice = variant ? variant.price_formatted : '';
    const size = sizeKey(this.#sizeValue());
    const orient = this.#state.orient;
    const cm = CM[orient][size];
    this.#boards().forEach((b) => {
      b.dataset.size = size;
    });
    const label = cm[0] + ' × ' + cm[1] + ' cm · ' + orient.toLowerCase();
    this.section.querySelectorAll('[data-btf-size-label]').forEach((el) => (el.textContent = label));
    const open = this.#sizeOpen();
    const price = open ? this.variants.from || '' : variant ? variant.price_formatted : '';
    this.section.querySelectorAll('[data-btf-price]').forEach((el) => (el.textContent = price));
    // "Add to basket — £145": the label carries the price, as drawn
    this.section
      .querySelectorAll('[data-btf-basket-price]')
      .forEach((el) => (el.textContent = !open && variant && variant.available ? ' — ' + price : ''));
    this.#text();
  }

  // a radio group restored from a sibling page: check the one that matches
  #check(selector, test) {
    this.section.querySelectorAll(selector).forEach((i) => {
      if (test(i.value) && !i.checked) i.checked = true;
    });
  }

  #setOrient(value, silent) {
    this.#state.orient = orientKey(value);
    this.#boards().forEach((b) => {
      b.dataset.orient = this.#state.orient;
    });
    this.#check('[data-btf-orient]', (v) => orientKey(v) === this.#state.orient);
    if (!silent) this.#variant();
  }

  #setCap(value) {
    this.#state.cap = /black/i.test(value) ? 'black' : 'white';
    this.#boards().forEach((b) => {
      b.dataset.cap = this.#state.cap;
    });
    this.#check('[data-btf-cap]', (v) => (/black/i.test(v) ? 'black' : 'white') === this.#state.cap);
  }

  #setView(view) {
    this.#state.view = view === 'room' ? 'room' : 'product';
    if (this.board) this.board.dataset.view = this.#state.view;
    this.section.querySelectorAll('[data-btf-view]').forEach((tab) => {
      const on = tab.dataset.btfView === this.#state.view;
      tab.setAttribute('aria-pressed', on ? 'true' : 'false');
      tab.classList.toggle('is-on', on);
    });
  }
}

if (!customElements.get('btf-keepcalm-board')) customElements.define('btf-keepcalm-board', KeepCalmBoard);
if (!customElements.get('btf-keepcalm-preview')) customElements.define('btf-keepcalm-preview', KeepCalmPreview);
